import * as Icons from "lucide-react";

const CURRENCY_SYMBOLS = {
  GBP: "\u00a3",
  USD: "$",
  CAD: "$",
  AUD: "$",
  EUR: "\u20ac",
  JPY: "\u00a5",
  CNY: "\u00a5",
  INR: "\u20b9",
  CHF: "Fr",
  NZD: "$",
  HKD: "$",
  SGD: "$",
  ZAR: "R",
  MXN: "$",
  BRL: "R$",
};

function formatPrice(amount, currencyCode) {
  if (amount === null || amount === undefined) return "";
  const sym = CURRENCY_SYMBOLS[(currencyCode || "GBP").toUpperCase()] || "";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  if (Number.isInteger(n)) return `${sym}${n.toLocaleString()}`;
  return `${sym}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatArea(sqft, sqm, units = "both") {
  if (!sqft && !sqm) return null;
  const sf = sqft ?? Math.round(sqm / 0.092903);
  const sm = sqm ?? Math.round(sqft * 0.092903);
  if (units === "sqft") return `${sf.toLocaleString()} sq ft`;
  if (units === "sqm") return `${sm.toLocaleString()} m²`;
  return `${sf.toLocaleString()} sq ft / ${sm.toLocaleString()} m²`;
}

const DEFAULT_FIELDS = ["sqft", "bedrooms", "bathrooms", "reception_rooms"];

const ICONS = {
  sqft: Icons.Ruler,
  sqm: Icons.Ruler,
  bedrooms: Icons.Bed,
  bathrooms: Icons.Bath,
  reception_rooms: Icons.Sofa,
  garages: Icons.Car,
  year_built: Icons.Calendar,
  lot_sqft: Icons.Trees,
};

/**
 * Reusable listing stats card — used on SharePage and RealtorDashboard.
 * Shows price + icon row in the company's brand color.
 */
export default function ListingStatsCard({
  stats,
  brand,
  variant = "dark", // 'dark' (share page) | 'compact' (dashboard)
  showFields,
  units = "both",
  "data-testid": testId,
}) {
  if (!stats || (!stats.price && !hasAnyIcon(stats))) return null;
  const fields = showFields || DEFAULT_FIELDS;
  const accent = brand || "#F6D84B";

  const rows = [];
  for (const f of fields) {
    if (f === "sqft" || f === "sqm" || f === "area") {
      const txt = formatArea(stats.sqft, stats.sqm, units);
      if (txt) rows.push({ key: "sqft", text: txt });
    } else if (f === "bedrooms" && stats.bedrooms != null)
      rows.push({ key: "bedrooms", text: String(stats.bedrooms) });
    else if (f === "bathrooms" && stats.bathrooms != null)
      rows.push({ key: "bathrooms", text: String(stats.bathrooms) });
    else if (f === "reception_rooms" && stats.reception_rooms != null)
      rows.push({ key: "reception_rooms", text: String(stats.reception_rooms) });
    else if (f === "garages" && stats.garages != null)
      rows.push({ key: "garages", text: String(stats.garages) });
    else if (f === "year_built" && stats.year_built != null)
      rows.push({ key: "year_built", text: String(stats.year_built) });
    else if (f === "lot_sqft" && stats.lot_sqft != null)
      rows.push({ key: "lot_sqft", text: `${stats.lot_sqft.toLocaleString()} sq ft` });
  }

  const price = formatPrice(stats.price, stats.currency);
  const label = stats.price_label || "Guide price";

  if (variant === "compact") {
    // Thin inline strip for the realtor dashboard
    return (
      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-400 mt-1"
        data-testid={testId}
      >
        {price && (
          <span className="inline-flex items-center gap-1 font-mono tabular-nums">
            <span className="text-[9px] uppercase tracking-widest text-zinc-600">{label}</span>
            <span style={{ color: accent }} className="text-xs font-medium">{price}</span>
          </span>
        )}
        {rows.map((r) => {
          const I = ICONS[r.key] || Icons.Circle;
          return (
            <span key={r.key} className="inline-flex items-center gap-1">
              <I size={10} style={{ color: accent }} />
              {r.text}
            </span>
          );
        })}
      </div>
    );
  }

  // Full dark "card" variant — used on the public share page
  return (
    <div
      className="relative overflow-hidden rounded-2xl border mt-6 mb-6 px-6 py-5"
      style={{
        background: "linear-gradient(180deg, #121a2e 0%, #0d1424 100%)",
        borderColor: brand ? `${brand}40` : "rgba(255,255,255,0.08)",
      }}
      data-testid={testId}
    >
      {price && (
        <>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-1">
            {label}
          </div>
          <div
            className="font-display text-4xl sm:text-5xl mb-3 leading-none"
            style={{ color: accent }}
          >
            {price}
          </div>
        </>
      )}
      {rows.length > 0 && (
        <>
          <div className="h-px bg-white/10 my-4" />
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            {rows.map((r) => {
              const I = ICONS[r.key] || Icons.Circle;
              return (
                <div key={r.key} className="flex items-center gap-2">
                  <I size={16} style={{ color: accent }} />
                  <span className="text-white font-medium text-sm">{r.text}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function hasAnyIcon(stats) {
  return [
    "sqft", "sqm", "bedrooms", "bathrooms", "reception_rooms",
    "garages", "year_built", "lot_sqft",
  ].some((k) => stats[k] != null);
}
